'use client'

import React, { useState } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@dc-shared-web/components/shadcn/popover'
import {
  Command,
  CommandGroup,
  CommandItem,
} from '@dc-shared-web/components/shadcn/command'
import { Icon } from '@dc-shared-web/components/base'
import {
  faChevronDown,
  IconDefinition,
} from '@document-crunch/pro-regular-svg-icons'
import { cn } from '@dc-shared-web/lib'
interface ChipSelectOption {
  value: string
  label: string
  description: string
  icon: IconDefinition
  color: ChipSelectIconColor
  disabled?: boolean
  isRemoveOption?: boolean
}
export enum ChipSelectIconColor {
  blue = 'blue',
  aqua = 'aqua',
  neutral = 'neutral',
  purple = 'purple',
  orange = 'orange',
  red = 'red',
  yellow = 'yellow',
  green = 'green',
}

interface ChipSelectProps {
  options: ChipSelectOption[]
  selectedValue: string | null | undefined
  onChange: (value: string) => void
  placeholder: string
  icon?: IconDefinition
  color?: ChipSelectIconColor
}

const colorClasses = {
  blue: 'tw-text-blue-700 tw-bg-blue-200',
  aqua: 'tw-text-secondary-700 tw-bg-secondary-100',
  neutral: 'tw-text-neutral-600 tw-bg-neutral-100',
  purple: 'tw-text-purple-700 tw-bg-purple-100',
  orange: 'tw-text-orange-700 tw-bg-orange-100',
  red: 'tw-text-destructive-700 tw-bg-destructive-200',
  yellow: 'tw-text-warning-700 tw-bg-warning-200',
  green: 'tw-text-success-700 tw-bg-success-200',
}

export interface ChipItemProps {
  icon: IconDefinition
  label: string
  textColor: string
  bgColor: string
  suffixIcon?: IconDefinition
}

const ChipItem: React.FC<ChipItemProps> = ({
  icon,
  label,
  textColor,
  bgColor,
  suffixIcon,
}) => (
  <div
    className={cn(
      'tw-flex tw-items-center tw-rounded-full tw-px-3 tw-py-2 tw-cursor-pointer tw-text-sm',
      textColor,
      bgColor
    )}
  >
    <Icon icon={icon} size='sm' className={textColor} />
    <span className='tw-text-xs tw-ml-2'>{label}</span>
    {suffixIcon && (
      <Icon icon={suffixIcon} size='sm' className={cn('tw-ml-2', textColor)} />
    )}
  </div>
)

export const ChipSelect: React.FC<ChipSelectProps> = ({
  options,
  selectedValue,
  onChange,
  placeholder,
  icon,
  color = 'neutral',
}) => {
  const [open, setOpen] = useState(false)
  const selectedOption = options.find(
    (option) => option.value === selectedValue
  )
  const [textColor, bgColor] =
    colorClasses[color as keyof typeof colorClasses].split(' ')

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className='tw-relative'>
          <ChipItem
            icon={icon || faChevronDown}
            label={selectedOption?.label || placeholder}
            textColor={textColor}
            bgColor={bgColor}
            suffixIcon={faChevronDown}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className='tw-w-[300px] tw-p-0'>
        <Command>
          <CommandGroup>
            {options.map((option) => {
              const [optionTextColor, optionBgColor] =
                colorClasses[option.color].split(' ')
              return (
                <CommandItem
                  key={option.value}
                  onSelect={() => {
                    onChange(option.value)
                    setOpen(false)
                  }}
                  className={cn(
                    'tw-flex tw-flex-col tw-items-start tw-p-2 tw-mb-2',
                    selectedValue === option.value && 'tw-bg-neutral-100',
                    option.disabled && 'tw-opacity-50 tw-cursor-not-allowed'
                  )}
                  disabled={option.disabled}
                >
                  <ChipSelectOptionContent
                    option={option}
                    textColor={optionTextColor}
                    bgColor={optionBgColor}
                  />
                </CommandItem>
              )
            })}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface ChipSelectOptionProps {
  option: ChipSelectOption
  textColor: string
  bgColor: string
}

const ChipSelectOptionContent: React.FC<ChipSelectOptionProps> = ({
  option,
  textColor,
  bgColor,
}) => {
  return (
    <>
      {option.isRemoveOption ? (
        <div className='tw-text-sm tw-text-neutral-400'>{option.label}</div>
      ) : (
        <>
          <ChipItem
            icon={option.icon}
            label={option.label}
            textColor={textColor}
            bgColor={bgColor}
          />
          <div className='tw-text-xs tw-text-neutral-300 tw-mt-1'>
            {option.description}
          </div>
        </>
      )}
    </>
  )
}
